/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';

import { apiRequest, authorizedApiRequest } from './ApiRequest';
import ApiErrorModel from '../models/ApiErrorModel';
import UserModel from '../models/UserModel';
import { ConsentType } from '../models/PolicyModel';
import AppUtils from '../utilities/AppUtils';

const UserApi = {
  create() {
    return apiRequest('post', 'users');
  },
  checkEmailExists(email) {
    return apiRequest('head', `users?email=${encodeURIComponent(email)}`);
  },
  get(id) {
    return authorizedApiRequest('get', `users/${id}`);
  },
  update(id) {
    return authorizedApiRequest('put', `users/${id}`);
  },
  del(id) {
    return authorizedApiRequest('delete', `users/${id}`);
  },
  setMobileNumber(id) {
    return authorizedApiRequest('put', `users/${id}/mfa`);
  },
  enableMfa(id) {
    return authorizedApiRequest('delete', `users/${id}/mfa/enable`);
  },
  disableMfa(id) {
    return authorizedApiRequest('delete', `users/${id}/mfa`);
  },
  requestEmailChange(id) {
    return authorizedApiRequest('put', `users/${id}/pendingEmail`);
  },
  deletePendingEmailChange(id) {
    return authorizedApiRequest('delete', `users/${id}/pendingEmail`);
  },
  unsubscribe(cuid) {
    return apiRequest('put', `public/users/${cuid}/communication/unsubscribe`);
  },
};

const UserService = {
  create(userData, callback) {
    return UserApi.create()
      .send(_.assign({}, { subdomain: AppUtils.getSubdomain() }, userData))
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, new UserModel(res.body.data), res);
      });
  },

  checkEmailExists(email, callback) {
    return UserApi.checkEmailExists(email)
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, (res.status === 204));
      });
  },

  activate(userId, token, password, callback) {
    return UserApi.activate(userId, token)
      .send({
        password,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, new UserModel(res.body.data));
      });
  },

  get(userId, callback) {
    return UserApi.get(userId).end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, new UserModel(res.body.data));
    });
  },

  update(userData, callback) {
    return UserApi.update(userData.id).send(userData).end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  del(userId, reasonData = {}, callback) {
    return UserApi.del(userId).send(reasonData).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  setMobileNumber(userId, mfaSettings, callback) {
    return UserApi.setMobileNumber(userId).send(mfaSettings).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError);
    });
  },

  enableMfa(userId, mfaSettings, callback) {
    return UserApi.enableMfa(userId).send(mfaSettings).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError);
    });
  },

  disableMfa(userId, callback) {
    return UserApi.disableMfa(userId).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError);
    });
  },

  requestEmailChange(userId, email, callback) {
    return UserApi.requestEmailChange(userId)
      .send({ email, subdomain: AppUtils.getSubdomain() })
      .end(err => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError);
      });
  },

  deletePendingEmailChange(userId, callback) {
    return UserApi.deletePendingEmailChange(userId).end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError);
    });
  },

  confirmEmailVerification(token) {
    return apiRequest('put', 'users/email/verify').set(
      'Authorization',
      `Bearer ${token}`
    ).then(() => {
      return null;
    });
  },

  getUserActionItems(userId) {
    return authorizedApiRequest('get', `users/${userId}/actionItems`)
    .then((res) => {
      return res.body.data;
    });
  },

  getNewUserActionItems() {
    return authorizedApiRequest('get', 'users/actionItems')
    .then((res) => {
      return res.body.data;
    });
  },

  provideConsent(userId, policyIdList) {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.PROVIDED,
    }));

    return authorizedApiRequest('post', `users/${userId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  withdrawConsent(userId, policyIdList) {
    const policies = policyIdList.map(p => ({
      id: p,
      consent_type: ConsentType.WITHHELD,
    }));

    return authorizedApiRequest('post', `users/${userId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  consent(userId, policies) {
    return authorizedApiRequest('post', `users/${userId}/consent`)
    .send({ policies })
    .then((res) => {
      return res.body.data;
    });
  },

  unsubscribe(cuid, data, callback) {
    return UserApi.unsubscribe(cuid)
      .send(data)
      .end(err => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError);
      });
  },

  verifyEmail(userId) {
    return authorizedApiRequest('post', `users/${userId}/email/verify`)
      .send({ subdomain: AppUtils.getSubdomain() })
      .then((err, res) => {
        return res;
      });
  },

  cancelPendingEmail(userId) {
    return authorizedApiRequest('delete', `users/${userId}/pendingEmail`)
      .then((err, res) => {
        return res;
      });
  },

};

export default UserService;
